import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  Collapse,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Info as InfoIcon,
  TrendingUp as TrendIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterIcon,
  Download as ExportIcon,
  Lightbulb as InsightIcon
} from '@mui/icons-material';

// Types for rule contributions
interface RuleContribution {
  ruleId: string;
  ruleName: string;
  category: 'spectral' | 'structural' | 'geochemical' | 'geological' | 'temporal';
  weight: number;
  contribution: number; // 0-100
  confidence: number; // 0-1
  triggeredLocations: number;
  description: string;
  threshold?: {
    value: number;
    operator: '>' | '<' | '>=' | '<=' | '==';
    unit?: string;
  };
  statistics?: {
    mean: number;
    std: number;
    min: number;
    max: number;
  };
}

interface LocationContribution {
  locationId: string;
  coordinates: { lat: number; lon: number };
  totalScore: number;
  rules: {
    ruleId: string;
    contribution: number;
    value: number;
  }[];
}

interface RuleContributionPanelProps {
  analysisId?: string;
  ruleContributions?: RuleContribution[];
  locationContributions?: LocationContribution[];
  onRuleSelect?: (ruleId: string) => void;
  onLocationSelect?: (location: LocationContribution) => void;
}

// Category colors
const CATEGORY_COLORS = {
  spectral: 'var(--palantir-blue-600)',
  structural: '#00D2FF',
  geochemical: 'var(--palantir-status-success)',
  geological: '#FFD93D',
  temporal: '#B388FF'
};

// Category icons and descriptions
const CATEGORY_INFO = {
  spectral: {
    icon: '🛰️',
    description: 'Satellite-based spectral alterations'
  },
  structural: {
    icon: '🗺️',
    description: 'Structural geology and faults'
  },
  geochemical: {
    icon: '🧪',
    description: 'Geochemical anomalies and pathfinders'
  },
  geological: {
    icon: '🪨',
    description: 'Host rock and geological settings'
  },
  temporal: {
    icon: '📅',
    description: 'Time-series and change detection'
  }
};

const RuleContributionPanel: React.FC<RuleContributionPanelProps> = ({
  analysisId,
  ruleContributions = [],
  locationContributions = [],
  onRuleSelect,
  onLocationSelect
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'contribution' | 'confidence' | 'locations'>('contribution');
  const [showOnlyTriggered, setShowOnlyTriggered] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  // Filter and sort rules
  const filteredRules = useMemo(() => {
    let rules = [...ruleContributions];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      rules = rules.filter(rule => rule.category === selectedCategory);
    }
    
    // Filter only triggered rules
    if (showOnlyTriggered) {
      rules = rules.filter(rule => rule.triggeredLocations > 0);
    }
    
    // Sort rules
    rules.sort((a, b) => {
      switch (sortBy) {
        case 'contribution':
          return b.contribution - a.contribution;
        case 'confidence':
          return b.confidence - a.confidence;
        case 'locations':
          return b.triggeredLocations - a.triggeredLocations;
        default:
          return 0;
      }
    });
    
    return rules;
  }, [ruleContributions, selectedCategory, showOnlyTriggered, sortBy]);

  // Calculate category statistics
  const categoryStats = useMemo(() => {
    const stats: Record<string, { count: number; totalContribution: number; avgConfidence: number }> = {};
    
    Object.keys(CATEGORY_COLORS).forEach(category => {
      const categoryRules = ruleContributions.filter(rule => rule.category === category);
      stats[category] = {
        count: categoryRules.length,
        totalContribution: categoryRules.reduce((sum, rule) => sum + rule.contribution, 0),
        avgConfidence: categoryRules.length > 0 
          ? categoryRules.reduce((sum, rule) => sum + rule.confidence, 0) / categoryRules.length 
          : 0
      };
    });
    
    return stats;
  }, [ruleContributions]);

  // Toggle rule expansion
  const toggleRuleExpansion = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  // Export rule contributions
  const handleExport = () => {
    const exportData = {
      analysisId,
      timestamp: new Date().toISOString(),
      rules: filteredRules.map(rule => ({
        name: rule.ruleName,
        category: rule.category,
        contribution: rule.contribution,
        confidence: rule.confidence,
        triggeredLocations: rule.triggeredLocations,
        weight: rule.weight
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rule_contributions_${analysisId || 'analysis'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{
            color: 'var(--palantir-blue-600)',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            RULE CONTRIBUTION ANALYSIS
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export Contributions">
              <IconButton size="small" onClick={handleExport} sx={{ color: 'var(--palantir-text-muted)' }}>
                <ExportIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="About Rule Contributions">
              <IconButton size="small" sx={{ color: 'var(--palantir-text-muted)' }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {analysisId && (
          <Typography sx={{
            color: 'var(--palantir-text-muted)',
            fontFamily: 'monospace',
            fontSize: '10px'
          }}>
            Analysis ID: {analysisId}
          </Typography>
        )}
      </Box>

      {/* Category Overview */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Typography sx={{
          color: '#00D2FF',
          fontFamily: 'monospace',
          fontSize: '12px',
          mb: 1
        }}>
          CATEGORY BREAKDOWN
        </Typography>
        <Grid container spacing={1}>
          {Object.entries(categoryStats).map(([category, stats]) => (
            <Grid size={{ xs: 12, sm: 6 }} key={category}>
              <Card 
                onClick={() => setSelectedCategory(category)}
                sx={{ 
                  backgroundColor: selectedCategory === category ? '#21262D' : '#0D1117',
                  border: `1px solid ${selectedCategory === category ? CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] : '#30363D'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#21262D',
                    borderColor: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS]
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '16px' }}>
                      {CATEGORY_INFO[category as keyof typeof CATEGORY_INFO].icon}
                    </Typography>
                    <Typography sx={{
                      color: 'var(--palantir-text-primary)',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      textTransform: 'capitalize'
                    }}>
                      {category}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '9px'
                    }}>
                      {stats.count} rules
                    </Typography>
                    <Typography sx={{
                      color: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS],
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                      {stats.totalContribution.toFixed(1)}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Controls */}
      <Box sx={{ p: 2, borderBottom: '1px solid #30363D' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'var(--palantir-text-muted)', fontSize: '12px' }}>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#30363D'
                  }
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {Object.keys(CATEGORY_COLORS).map(category => (
                  <MenuItem key={category} value={category}>
                    {CATEGORY_INFO[category as keyof typeof CATEGORY_INFO].icon} {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'var(--palantir-text-muted)', fontSize: '12px' }}>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#30363D'
                  }
                }}
              >
                <MenuItem value="contribution">Contribution</MenuItem>
                <MenuItem value="confidence">Confidence</MenuItem>
                <MenuItem value="locations">Triggered Locations</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyTriggered}
                  onChange={(e) => setShowOnlyTriggered(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '11px', fontFamily: 'monospace' }}>
                  Triggered Only
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rules List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {filteredRules.length === 0 ? (
          <Alert severity="info" sx={{ backgroundColor: '#21262D', color: 'var(--palantir-text-primary)' }}>
            No rules match the current filters
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredRules.map((rule) => (
              <Paper
                key={rule.ruleId}
                sx={{
                  backgroundColor: '#161B22',
                  border: '1px solid #30363D',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: CATEGORY_COLORS[rule.category]
                  }
                }}
              >
                {/* Rule Header */}
                <Box
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    backgroundColor: rule.triggeredLocations > 0 ? '#161B22' : '#0D1117'
                  }}
                  onClick={() => toggleRuleExpansion(rule.ruleId)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 4,
                          height: 40,
                          backgroundColor: CATEGORY_COLORS[rule.category],
                          borderRadius: '2px'
                        }}
                      />
                      <Box>
                        <Typography sx={{
                          color: 'var(--palantir-text-primary)',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {rule.ruleName}
                        </Typography>
                        <Typography sx={{
                          color: 'var(--palantir-text-muted)',
                          fontFamily: 'monospace',
                          fontSize: '10px'
                        }}>
                          {rule.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={`${rule.contribution.toFixed(1)}%`}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--palantir-blue-600)',
                          color: '#000',
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}
                      />
                      <IconButton size="small">
                        {expandedRules.has(rule.ruleId) ? <CollapseIcon /> : <ExpandIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Progress Bar */}
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={rule.contribution}
                      sx={{
                        height: 6,
                        borderRadius: '3px',
                        backgroundColor: '#30363D',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: CATEGORY_COLORS[rule.category]
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Quick Stats */}
                  <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '9px'
                    }}>
                      Weight: <span style={{ color: 'var(--palantir-text-primary)' }}>{rule.weight}</span>
                    </Typography>
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '9px'
                    }}>
                      Confidence: <span style={{ color: 'var(--palantir-text-primary)' }}>{(rule.confidence * 100).toFixed(0)}%</span>
                    </Typography>
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '9px'
                    }}>
                      Locations: <span style={{ color: '#00D2FF' }}>{rule.triggeredLocations}</span>
                    </Typography>
                  </Box>
                </Box>

                {/* Expanded Details */}
                <Collapse in={expandedRules.has(rule.ruleId)}>
                  <Divider sx={{ borderColor: '#30363D' }} />
                  <Box sx={{ p: 2 }}>
                    {/* Threshold Information */}
                    {rule.threshold && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{
                          color: '#00D2FF',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          mb: 0.5
                        }}>
                          TRIGGER THRESHOLD
                        </Typography>
                        <Typography sx={{
                          color: 'var(--palantir-text-primary)',
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          backgroundColor: '#0D1117',
                          p: 1,
                          borderRadius: '4px'
                        }}>
                          Value {rule.threshold.operator} {rule.threshold.value} {rule.threshold.unit || ''}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Statistics */}
                    {rule.statistics && (
                      <Box>
                        <Typography sx={{
                          color: '#00D2FF',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          mb: 0.5
                        }}>
                          VALUE STATISTICS
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 3 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px' }}>Mean</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                              {rule.statistics.mean.toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px' }}>Std Dev</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                              {rule.statistics.std.toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px' }}>Min</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                              {rule.statistics.min.toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 3 }}>
                            <Typography sx={{ color: 'var(--palantir-text-muted)', fontSize: '9px' }}>Max</Typography>
                            <Typography sx={{ color: 'var(--palantir-text-primary)', fontSize: '10px', fontFamily: 'monospace' }}>
                              {rule.statistics.max.toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    
                    {/* Action Button */}
                    {onRuleSelect && (
                      <Button
                        onClick={() => onRuleSelect(rule.ruleId)}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{
                          mt: 2,
                          borderColor: '#30363D',
                          color: '#00D2FF',
                          fontFamily: 'monospace',
                          fontSize: '10px',
                          '&:hover': {
                            borderColor: '#00D2FF',
                            backgroundColor: 'rgba(0, 210, 255, 0.1)'
                          }
                        }}
                      >
                        View Rule Details
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Insights Panel */}
      {filteredRules.length > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid #30363D', backgroundColor: '#0D1117' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InsightIcon sx={{ color: '#FFD93D', fontSize: '16px' }} />
            <Typography sx={{
              color: '#FFD93D',
              fontFamily: 'monospace',
              fontSize: '11px',
              fontWeight: 'bold'
            }}>
              KEY INSIGHTS
            </Typography>
          </Box>
          <Typography sx={{
            color: 'var(--palantir-text-primary)',
            fontFamily: 'monospace',
            fontSize: '10px',
            lineHeight: 1.6
          }}>
            • Top contributor: <span style={{ color: 'var(--palantir-blue-600)' }}>{filteredRules[0]?.ruleName}</span> ({filteredRules[0]?.contribution.toFixed(1)}%)
            <br />
            • Most confident: <span style={{ color: '#00D2FF' }}>{filteredRules.sort((a, b) => b.confidence - a.confidence)[0]?.ruleName}</span>
            <br />
            • Total triggered locations: <span style={{ color: 'var(--palantir-status-success)' }}>{filteredRules.reduce((sum, rule) => sum + rule.triggeredLocations, 0)}</span>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RuleContributionPanel;